'use client';

import { cn } from '@reelplicate/ui/lib/utils';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import * as React from 'react';

import { tv } from '../../lib/tv';

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('grid gap-3', className)}
      {...props}
    />
  );
}

/**
 * The Figma Radio "Circle" (Controls ↳ Radio, 48:84), keyed on the attributes
 * Radix sets (`data-state`, `data-disabled`).
 *
 * Off: 1.5px border/strong inside stroke on bg/surface; hover border/focus.
 * The stroke is an inset ring, not a border: Chromium rounds a 1.5px border
 * down to 1px at 1x density.
 * On: brand/strong fill (hover brand/strong-hover) with a 7px icon/inverse dot.
 * Focus: the inside stroke is replaced by a 2px border/focus outside stroke.
 * Disabled: bg/disabled + border/default, or bg/emphasis + icon/tertiary dot.
 * Motion: the dot stays mounted and pops in (scale 0.4 → 1 with a slight
 * overshoot, 180ms) instead of appearing; reduced motion fades it only.
 */
/**
 * The checked mark inside a Radio or Choice Card control: always mounted,
 * shown by the nearest `data-state` (the control's own, or the card's).
 */
export const selectionMarkPop = [
  'transition-[scale,opacity] duration-180 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
  'motion-reduce:scale-100 motion-reduce:transition-opacity',
].join(' ');

export const radioCircle = tv({
  base: [
    'peer inline-flex size-[18px] shrink-0 items-center justify-center rounded-full',
    'inset-ring-[1.5px] inset-ring-border-strong bg-bg-surface text-icon-inverse outline-none',
    'transition-[background-color,box-shadow] duration-[120ms] ease-out',
    'enabled:hover:inset-ring-border-focus',
    'data-[state=checked]:inset-ring-0 data-[state=checked]:bg-brand-strong data-[state=checked]:enabled:hover:bg-brand-strong-hover',
    'focus-visible:inset-ring-0 focus-visible:ring-2 focus-visible:ring-border-focus',
    'data-disabled:inset-ring-border-default data-disabled:bg-bg-disabled data-disabled:text-icon-tertiary',
    'data-disabled:data-[state=checked]:bg-bg-emphasis',
    'disabled:cursor-not-allowed',
  ],
});

/** shadcn RadioGroupItem (Radix), restyled to the Figma Radio circle. */
function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={radioCircle({ className })}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        forceMount
        data-slot="radio-group-indicator"
        className={cn(
          'size-[7px] rounded-full bg-current',
          'data-[state=unchecked]:scale-40 data-[state=unchecked]:opacity-0',
          selectionMarkPop,
        )}
      />
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
